import React from "react";
import { BaseCard, SVGIcon } from "../baseCard/baseCard.tsx";
import { getFontSizes, getTitleHeight, darkenColor } from "../baseCard/BaseCardUtils.ts";
import "./cardList.css";

interface CardListItem {
  label: string;
  value: number | string;
  unit?: string;
}

interface CardListProps {
  category: string;
  title: string;
  items: CardListItem[];
  highlight?: boolean;
}

const CardList: React.FC<CardListProps> = ({
  category,
  title,
  items,
  highlight = false,
}) => {
  const fontSizes = getFontSizes(highlight);
  const titleHeight = getTitleHeight(highlight);
  const color = "var(--color-light-gray)";
  const shapeColor = darkenColor(color);

  const getValueColor = (value: number | string): string => {
    const numericValue =
      typeof value === "string"
        ? parseFloat(value.toString().replace(/[^\d.-]/g, ""))
        : value;

    if (numericValue > 0) {
      return "var(--color-green)";
    } else if (numericValue < 0) {
      return "var(--color-orange)";
    } else {
      return "var(--color-dark-blue)";
    }
  };

  const svgPaths = {
    topShape: "M20.7637 1.11044e-05C9.71799 1.24175e-05 0.76368 8.95433 0.763673 20L0.763678 97.4131C9.37694 104.498 32.5399 112.037 67.7002 90.1162C103.94 67.5225 112.736 17.7127 114.172 0L20.7637 1.11044e-05Z",
    bottomShape: "M206.493 109.598C206.493 120.643 197.539 129.598 186.493 129.598H0.626953C8.79625 105.382 42.9206 56.9502 114.063 56.9502C185.206 56.9502 205.326 19.2817 206.493 0.447266V109.598Z"
  };

  return (
    <BaseCard highlight={highlight} color={color} className="cardList-card">
      <SVGIcon
        className="baseCard-icon-top-right"
        viewBox="0 0 115 105"
        fillColor={shapeColor}
        path={svgPaths.topShape}
      />

      <SVGIcon
        className="baseCard-icon-bottom-left"
        viewBox="0 0 207 130"
        fillColor={shapeColor}
        path={svgPaths.bottomShape}
      />

      <div className="baseCard-content cardList-content">
        <div className="cardList-header">
          <div
            className="cardList-category"
            style={{ fontSize: fontSizes.category }}
          >
            {category.toUpperCase()}
          </div>

          <div
            className="cardList-title"
            style={{
              fontSize: fontSizes.title,
              height: titleHeight,
            }}
          >
            {title}
          </div>

          <div
            className="baseCard-divider cardList-divider"
            style={{
              width: "calc(100% + 2 * var(--spacing-3))",
            }}
          ></div>
        </div>

        <div 
          className="cardList-table"
          style={{
            gridTemplateRows: `repeat(${items.length}, 1fr)`
          }}
        >
          {items.map((item, index) => (
            <div 
              key={index} 
              className="cardList-row"
            >
              <div className="cardList-row-content">
                <span
                  className="cardList-label"
                  style={{ fontSize: fontSizes.listItem }}
                >
                  {item.label}
                </span>
                <span
                  className="cardList-value"
                  style={{
                    fontSize: fontSizes.listItem,
                    color: getValueColor(item.value),
                  }}
                >
                  {item.value}
                  {item.unit || ""}
                </span>
              </div>
              {index < items.length - 1 && <div className="cardList-row-divider" />}
            </div>
          ))}
        </div>
      </div>
    </BaseCard>
  );
};

export default CardList;